/**
 * The PvP half of the Meta page: GO Battle League rankings.
 *
 * Extracted from BattleHub when Raids became its own mode. It owns everything
 * league-shaped — the CP caps, the Poké Ball chips, PvPoke's tier bands — and
 * knows nothing about raids, which are a different question over different
 * data (see RaidMetaPanel).
 *
 * The rankings are PvPoke's, under MIT, and the credit at the foot is not
 * optional: it is the licence's condition and the reader's way of knowing
 * whose judgement they are looking at.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Lock, ScanSearch } from "lucide-react";
import { moveTypeFor } from "@/lib/moveTypeLookup";

import { CaughtSprite } from "@/components/CaughtSprite";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { useMyPokemonRecords } from "@/lib/myPokemonStore";
import { comparePvpEntry, compareLeagueKey } from "@/lib/metaCompare";
import {
  armBattleHubReturn,
  getBattleHubMemory,
  setBattleHubPvp,
} from "@/lib/battleHubMemory";
import { getPageScrollTop } from "@/lib/pageScroll";
import { MetaCompareBox } from "./MetaCompareBox";
import { MetaSearchButton } from "./MetaSearchButton";
import {
  BATTLE_META,
  BATTLE_META_ATTRIBUTION,
} from "@/lib/generated/battleMeta.generated";
import {
  BATTLE_META_MEASURES,
  DEFAULT_MEASURE,
  entriesForMeasure,
  leagueByKey,
  measureByKey,
  rankWithinFilter,
  regionsInLeague,
  typeRanksByEntry,
  typesInLeague,
  type BattleMetaEntry,
  type MetaFilters,
  type TypeRank,
} from "@/lib/battleMeta";
import { isPokeType } from "@/lib/types";
import { TypeIcon } from "@/components/TypeIcon";
import { ChipRow, MoveChip, TypeBadge, regionLabel, typeColor, typeLabel } from "./metaChrome";
import { SpineDot } from "@/components/SectionHeadingDot";

/** Tier colours. Deliberately the app's own tokens, not a new palette. */
const TIER_STYLE: Record<string, { bg: string; fg: string }> = {
  S: { bg: "var(--accent-fill)", fg: "var(--text-on-accent)" },
  A: { bg: "var(--accent-fill-glow)", fg: "var(--text-ink-bright)" },
  B: { bg: "var(--surface-raised)", fg: "var(--text-secondary)" },
  C: { bg: "var(--surface-sunken)", fg: "var(--text-tertiary)" },
};

/**
 * The leagues in their own colours: Great is blue, Ultra is gold, Master is
 * purple, which is how a GBL player already reads them.
 *
 * The glyph is a Poké Ball drawn in CSS rather than Niantic's league artwork —
 * their icons are theirs, and a coloured ball reads as "league" to anyone who
 * plays without borrowing an asset we have no right to ship.
 */
const LEAGUE_STYLE: Record<string, { color: string; band?: string; label: string }> = {
  great: { color: "hsl(207 90% 54%)", label: "Great" },
  /**
   * Ultra finishes in INK, not gold: the Ultra Ball is predominantly black with
   * a gold band, and gold alone read as the wrong ball (user, 2026-08-14).
   *
   * `--text-ink-bright` rather than a literal black, because a literal black
   * disappears on the dark theme. It resolves to near-black on light and
   * near-white on dark, so the chip stays legible either way and still reads as
   * the monochrome one next to a blue and a purple. The gold survives as the
   * ball's band, which is where it is on the real thing.
   */
  ultra: { color: "var(--text-ink-bright)", band: "hsl(45 93% 47%)", label: "Ultra" },
  master: { color: "hsl(280 68% 60%)", label: "Master" },
};

export function PvpMetaPanel() {
  // Applied values survive a round-trip to a record page (battleHubMemory):
  // a compare box opens the copy, and Back lands on the same league, measure
  // and filters the trainer left.
  const memory = useMemo(() => getBattleHubMemory().pvp, []);
  const [leagueKey, setLeagueKey] = useState(memory.leagueKey);
  const [measureKey, setMeasureKey] = useState<string>(memory.measureKey);
  const [filters, setFilters] = useState<MetaFilters>(memory.filters);
  const [compare, setCompare] = useState(memory.compare);
  useEffect(() => {
    setBattleHubPvp({ leagueKey, measureKey, filters, compare });
  }, [leagueKey, measureKey, filters, compare]);

  // "Compare your Pokémon to the meta" (2026-09-10 owner ask, Pro): a box
  // under every row saying where the trainer's own copies stand.
  const { tier, openPaywall } = useSession();
  const isPro = hasProAccess(tier);
  const records = useMyPokemonRecords();
  const [, setLocation] = useLocation();
  const compareLeague = compareLeagueKey(leagueKey);
  const comparing = compare && isPro && compareLeague != null;
  const toggleCompare = () => {
    if (!isPro) {
      openPaywall("meta-yours", "Compare your Pokémon to the meta with Pro");
      return;
    }
    setCompare((c) => !c);
  };
  const openRecord = (id: string) => {
    armBattleHubReturn(getPageScrollTop());
    setLocation(`/my-pokemon/${id}`);
  };

  const league = leagueByKey(leagueKey);
  const measure = measureByKey(measureKey);

  // EVERY derived list hangs off the measure, not off league.entries. A role
  // ranking is a different set of simulated battles, not the overall list
  // re-sorted, so its types, its regions and its "#1 Steel" badges are all its
  // own — reading any of them from `overall` would quietly mix two rankings.
  const ranked = useMemo(
    () => entriesForMeasure(league, measureKey),
    [league, measureKey],
  );
  const shown = useMemo(() => rankWithinFilter(ranked, filters), [ranked, filters]);
  // Computed over the whole measure, so a "#1 Steel" badge means the same
  // thing whatever filter happens to be on screen.
  const typeRanks = useMemo(() => typeRanksByEntry(ranked), [ranked]);
  const types = useMemo(() => typesInLeague(ranked), [ranked]);
  const regions = useMemo(() => regionsInLeague(ranked), [ranked]);

  const set = (patch: Partial<MetaFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const clear = () => setFilters({ type: "all", region: "all", tier: "all" });
  const filtered = filters.type !== "all" || filters.region !== "all";

  // See the note on the <p> below: this is deliberately one string, not a
  // template literal beside a static sentence.
  const countLine =
    (shown.length === ranked.length
      ? `Top ${ranked.length} ${measure.label.toLowerCase()} in ${league.label}.`
      : `${shown.length} of the top ${ranked.length} ${measure.label.toLowerCase()} in ${league.label}.`) +
    " The big number is where it sits in this filter; the small one is its place" +
    " in the whole league, which never moves.";

  return (
    <>
        {/* ONE BLOCK, not three. League, type and region were a card each,
            which pushed the list itself off the bottom of a phone: the point of
            the page is the ranking, and the controls were taking two thirds of
            the first screen. The tier filter is gone entirely — the badge on
            each row already says it, and nobody browses by tier. */}
        {/* Each block draws its OWN spine dot rather than relying on DotRail's
            `data-dotrail-dot`: the rail only reads markers off its direct
            children, and these sections are inside this component, so the markers
            it used to carry were never seen and the whole panel sat dotless
            against the rail. See SpineDot. */}
        <section className="relative v2-neon-card-soft p-3" data-testid="section-filters">
          <SpineDot />
          <div className="flex gap-1.5">
            {BATTLE_META.map((l) => {
              const style = LEAGUE_STYLE[l.key] ?? { color: "var(--accent-fill)", label: l.label };
              const on = l.key === leagueKey;
              return (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => setLeagueKey(l.key)}
                  aria-pressed={on}
                  data-testid={`league-${l.key}`}
                  className="flex-1 rounded-full px-2 py-1.5 text-[11px] font-bold inline-flex items-center justify-center gap-1.5"
                  style={{
                    background: on
                      ? `color-mix(in srgb, ${style.color} 22%, transparent)`
                      : "var(--surface-sunken)",
                    color: on ? style.color : "var(--text-tertiary)",
                    border: `1px solid ${on ? style.color : "var(--border-subtle)"}`,
                    minHeight: 34,
                  }}
                >
                  <LeagueBall
                    color={on ? style.color : "var(--text-tertiary)"}
                    band={on ? style.band : undefined}
                  />
                  {style.label}
                </button>
              );
            })}
          </div>

          {/* The MEASURE. Second only to the league, because "best lead" and
              "best overall" are different questions and the answer changes
              completely — Great League overall opens Lickilicky, leads opens
              Mimikyu at a clean 100. */}
          <div
            className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5"
            role="tablist"
            aria-label="Rank by"
            data-testid="measure-row"
          >
            {BATTLE_META_MEASURES.map((m) => {
              const on = m.key === measureKey;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setMeasureKey(m.key)}
                  data-testid={`measure-${m.key}`}
                  className="shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold"
                  style={{
                    background: on ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
                    color: on ? "var(--brand-green-on-light)" : "var(--text-tertiary)",
                    border: `1px solid ${on ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          <p
            className="mt-1.5 text-[10px] leading-snug px-0.5"
            style={{ color: "var(--text-secondary)" }}
            data-testid="measure-blurb"
          >
            {measure.blurb}
          </p>

          <ChipRow
            testId="type"
            options={types.map((t) => ({
              key: t,
              label: typeLabel(t),
              color: typeColor(t),
              icon: isPokeType(t) ? <TypeIcon type={t} size={13} title="" /> : undefined,
            }))}
            value={filters.type}
            onPick={(type) => set({ type })}
          />
          <ChipRow
            testId="region"
            options={regions.map((r) => ({ key: r, label: regionLabel(r) }))}
            value={filters.region}
            onPick={(region) => set({ region })}
          />

          {filtered && (
            <button
              type="button"
              onClick={clear}
              data-testid="filters-clear"
              className="mt-2 text-[10px] font-bold"
              style={{ color: "var(--accent-fill)" }}
            >
              Clear filters
            </button>
          )}
        </section>

        <CompareBlock on={comparing} locked={!isPro} onToggle={toggleCompare} testId="meta-compare-toggle" />

        <section className="relative mt-3" data-testid="section-meta-list">
          <SpineDot />
          {/* ONE text node, and a key that changes with it.
              This paragraph used to be a changing template literal followed by a
              static sentence — two adjacent text nodes in one <p>. Tapping a
              filter rewrites only the FIRST of them, but the new count is a
              different length so the whole paragraph re-wraps, and on iOS the
              line where the two nodes meet came back painted twice: the old
              raster at the old wrap position with the new one on top of it, the
              garbled middle line reported on device. Rebuilding the sentence as
              a single string means there is one node to update, and keying the
              <p> on its own text makes React replace the element rather than
              patch it, so the line boxes are laid out fresh instead of being
              repainted in place. (Not reproducible in the desktop preview — this
              targets the mechanism, not a symptom we can see here.) */}
          <p
            key={countLine}
            className="text-[11px] mb-2 px-1"
            style={{ color: "var(--text-secondary)" }}
            data-testid="meta-count"
          >
            {countLine}
          </p>

          {shown.length === 0 ? (
            <p
              className="v2-neon-card-soft p-4 text-[11.5px]"
              style={{ color: "var(--text-secondary)" }}
              data-testid="meta-empty"
            >
              Nothing in the top {ranked.length} matches that combination. Clear a filter
              and try again.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="meta-list">
              {shown.map((e) => (
                <li key={`${e.speciesId}-${e.rank}`}>
                  <MetaRow
                    entry={e}
                    filterRank={filtered ? e.filterRank : null}
                    typeRanks={typeRanks.get(e.speciesId) ?? []}
                    activeType={filters.type}
                  />
                  {comparing && compareLeague && (
                    <MetaCompareBox
                      compare={comparePvpEntry(records, compareLeague, e)}
                      testId={`meta-compare-${e.speciesId}`}
                      onOpen={openRecord}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* The credit. Required by the licence, and the reader's way of knowing
            whose judgement these rankings are. */}
        <p
          className="mt-4 mb-2 text-[10px] px-1"
          style={{ color: "var(--text-secondary)" }}
          data-testid="meta-attribution"
        >
          Rankings and movesets from{" "}
          <a
            href={BATTLE_META_ATTRIBUTION.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            {BATTLE_META_ATTRIBUTION.source}
          </a>
          , used under the {BATTLE_META_ATTRIBUTION.licence} licence. Snapshot taken{" "}
          {BATTLE_META_ATTRIBUTION.generatedOn}; the meta moves with the seasons.
        </p>
    </>
  );
}

/**
 * A Poké Ball in the league's colour: a top half, a band, a lighter bottom.
 *
 * `band` overrides the band colour for a ball whose stripe is not just the
 * surface showing through — Ultra's black-and-gold being the one that matters.
 */
function LeagueBall({ color, band }: { color: string; band?: string }) {
  const stripe = band ?? "var(--surface-raised)";
  return (
    <span
      className="shrink-0 w-3 h-3 rounded-full inline-block"
      style={{
        background: `linear-gradient(180deg, ${color} 0 45%, ${stripe} 45% 58%, color-mix(in srgb, ${color} 35%, transparent) 58% 100%)`,
        border: `1px solid ${color}`,
      }}
      aria-hidden
    />
  );
}

/**
 * One ranked Pokémon: where it sits, what it is FOR, and what it runs.
 *
 * TWO NUMBERS, NOT ONE. Filtering to Steel and renumbering 1..n would claim the
 * best Steel is the best Pokémon in the league; showing only the league rank
 * would leave the trainer counting rows to answer the question they just asked.
 * So the filtered position leads and the league rank sits under it in small
 * type, unmoved.
 *
 * THE TYPE BADGES ARE THE REAL DECISION. A dual-type at #12 overall might be
 * the best Steel in the league and the sixth-best Ice, which is exactly the
 * difference between building it and leaving it. The badge for the type being
 * filtered on is lit; the others stay quiet but visible, because "it is also
 * your best Rock" is the argument for building it once instead of twice.
 */
/**
 * The compare switch, as its own full-width block under the filters (owner
 * ask, second pass: "we need to show it's there with presence"). One big
 * button: filled in the accent when on, outlined when off, and locked for
 * Basic, where the tap opens the paywall rather than a wall of red boxes and
 * the block says so before the tap.
 */
export function CompareBlock({
  on,
  locked,
  onToggle,
  testId,
}: {
  on: boolean;
  locked: boolean;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <section className="relative mt-3" data-testid={`${testId}-block`}>
      <SpineDot />
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={on}
        data-testid={testId}
        className="w-full rounded-2xl px-4 py-3 flex items-center gap-3 text-left transition-transform active:scale-[0.99]"
        style={
          on
            ? {
                background: "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
                border: "1px solid var(--accent-fill)",
                boxShadow: "0 0 14px var(--accent-fill-glow)",
                color: "var(--text-on-accent)",
              }
            : {
                background: "var(--accent-fill-glow-faint)",
                border: "1.5px solid var(--accent-fill)",
                boxShadow: "0 0 0 1px var(--accent-fill-glow) inset",
                color: "var(--text-ink-bright)",
              }
        }
        title="A box under every row: your best copy for it, and whether it runs the ranked moves"
      >
        <span
          className="shrink-0 w-10 h-10 rounded-full grid place-items-center"
          style={
            on
              ? { background: "hsl(0 0% 100% / 0.18)" }
              : { background: "var(--accent-fill-glow-faint)", border: "1px solid var(--accent-fill-glow)" }
          }
        >
          <ScanSearch className="w-5 h-5" style={{ color: on ? "var(--text-on-accent)" : "var(--accent-fill)" }} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-black leading-tight">
            Compare your Pokémon to the meta
          </span>
          <span
            className="block text-[10.5px] leading-snug mt-0.5"
            style={{ color: on ? "var(--text-on-accent)" : "var(--text-secondary)", opacity: on ? 0.9 : 1 }}
          >
            {on
              ? "On. Under every row: your best copy, its moves, and how good it is."
              : "Under every row: your best copy, its moves, and how good it is."}
          </span>
        </span>
        <span
          className="shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide"
          style={
            on
              ? { background: "hsl(0 0% 100% / 0.18)", color: "var(--text-on-accent)" }
              : { background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", color: locked ? "var(--brand-green-on-light)" : "var(--text-secondary)" }
          }
          data-testid={`${testId}-state`}
        >
          {locked && <Lock className="w-3 h-3" aria-hidden />}
          {locked ? "Pro" : on ? "On" : "Off"}
        </span>
      </button>
    </section>
  );
}

function MetaRow({
  entry,
  filterRank,
  typeRanks,
  activeType,
}: {
  entry: BattleMetaEntry;
  /** Position within the current filter, or null when nothing is filtered. */
  filterRank: number | null;
  typeRanks: TypeRank[];
  activeType: string;
}) {
  const tier = TIER_STYLE[entry.tier] ?? TIER_STYLE.C;
  return (
    <div
      className="v2-neon-card-soft px-3 py-2.5 flex items-center gap-3"
      data-testid={`meta-row-${entry.speciesId}`}
    >
      <span className="w-8 shrink-0 text-center">
        <span
          className="block text-[12px] font-black tabular-nums"
          style={{ color: "var(--text-ink-bright)" }}
          data-testid={`meta-rank-${entry.speciesId}`}
        >
          {filterRank ?? entry.rank}
        </span>
        {filterRank != null && (
          <span
            className="block text-[8px] font-bold tabular-nums"
            style={{ color: "var(--text-secondary)" }}
            title={`#${entry.rank} in the whole league`}
            data-testid={`meta-league-rank-${entry.speciesId}`}
          >
            #{entry.rank}
          </span>
        )}
      </span>

      {/* FULL COLOUR, always. PokemonSprite renders the dex-grid treatment,
          where an uncaught species is an outline — right on a page about YOUR
          collection, wrong on a page about what the meta IS. Ownership has no
          bearing here until My Pokémon exists. */}
      <CaughtSprite id={entry.dexId} size={40} />

      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
          {entry.name}
        </p>
        {/* The moveset is the actionable half: the ranking says who, this says
            what to build. Pills, in the move's type colour, so the compare box
            beneath can wear the same pills and the two lines read together. */}
        <div className="mt-1 flex flex-wrap gap-1">
          {entry.moves.length > 0 ? (
            entry.moves.map((m) => <MoveChip key={m} name={m} type={moveTypeFor(m) ?? "normal"} />)
          ) : (
            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>No recommended moveset</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {entry.shadow && (
            <span
              className="text-[8px] font-black uppercase tracking-wide px-1.5 py-px rounded-full"
              style={{ background: "hsl(280 70% 62% / 0.18)", color: "hsl(280 70% 62%)" }}
              title="The Shadow form, which is a different Pokémon to build"
              data-testid={`meta-shadow-${entry.speciesId}`}
            >
              Shadow
            </span>
          )}
          {typeRanks.map((tr) => {
            const lit = activeType === tr.type;
            return (
              <TypeBadge
                key={tr.type}
                type={tr.type}
                lit={lit}
                title={`#${tr.rank} of ${tr.outOf} ${typeLabel(tr.type)} in this league`}
                testId={`meta-typerank-${entry.speciesId}-${tr.type}`}
              >
                #{tr.rank} {typeLabel(tr.type)}
              </TypeBadge>
            );
          })}
        </div>
      </div>

      <MetaSearchButton
        row={{ dexId: entry.dexId, shadow: entry.shadow }}
        label={entry.name}
        testId={`meta-search-${entry.speciesId}`}
      />

      <span
        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black"
        style={{ background: tier.bg, color: tier.fg }}
        title={`Tier ${entry.tier}, score ${entry.score}`}
        data-testid={`meta-tier-${entry.speciesId}`}
      >
        {entry.tier}
      </span>
    </div>
  );
}
